const addToCart = () => {
  const purchaseOptions = document.querySelectorAll('.purchase-option');
  const addToCartButtons = document.querySelectorAll('.add-to-cart-button');

  if (!addToCartButtons.length) {
    return;
  }

  const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  // Kids PDP quantity stepper (+/- next to Add to Cart). Falls back to 1 when absent.
  const kidsQtyValueEl = document.querySelector('[data-kids-qty-value]');
  const getKidsQuantity = () => {
    if (!kidsQtyValueEl) return 1;
    const parsed = parseInt(kidsQtyValueEl.textContent, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };
  const setupKidsQtyStepper = () => {
    const stepper = document.querySelector('[data-kids-qty]');
    if (!stepper || !kidsQtyValueEl) return;
    const minusBtn = stepper.querySelector('[data-kids-qty-minus]');
    const plusBtn = stepper.querySelector('[data-kids-qty-plus]');
    const render = (n) => {
      kidsQtyValueEl.textContent = String(n);
      renderAtcTotal();
      renderShipBar();
    };
    if (minusBtn) minusBtn.addEventListener('click', () => render(Math.max(1, getKidsQuantity() - 1)));
    if (plusBtn) plusBtn.addEventListener('click', () => render(getKidsQuantity() + 1));
  };

  // Prefer the VISIBLE panel's selected option, so a hidden duplicate
  // purchase panel could never force the wrong selling plan on load.
  const getSelectedOption = () => {
    const selected = Array.from(document.querySelectorAll('.purchase-option.purchase-option--selected'));
    return selected.find((el) => el.offsetParent !== null) || selected[0] || null;
  };

  // ---------------------------------------------------------------------------
  // UK PDP Mini Refresh: line total on the Add to Cart button, and the live
  // free-shipping progress bar underneath it. Both read the price of whichever
  // purchase option is selected, so Subscribe & Save and One-time each show
  // their own total.
  // ---------------------------------------------------------------------------
  const atcTotalEl = document.querySelector('[data-atc-total]');
  const shipBar = document.querySelector('[data-pdp-shipbar]');
  const shipBarMsg = shipBar?.querySelector('[data-pdp-shipbar-msg]');
  const shipBarFill = shipBar?.querySelector('[data-pdp-shipbar-fill]');
  let cartTotalInCents = shipBar ? parseInt(shipBar.dataset.cartTotal, 10) || 0 : 0;

  // The cart section publishes the country's threshold (already in presentment
  // currency) on window.cartData from snippets/free-shipping-config.liquid —
  // the same snippet that rendered data-threshold here. Prefer the runtime
  // value so the bar can never disagree with the cart; fall back to the
  // server-rendered attribute if the cart section isn't on the page. Read it
  // lazily: window.cartData is defined by the cart section's inline script,
  // which may run after this module.
  const getShipBarConfig = () => {
    const cartData = window.cartData || {};
    const runtimeThreshold = Number(cartData.freeShippingThreshold);
    const attrThreshold = shipBar ? (parseInt(shipBar.dataset.threshold, 10) || 0) / 100 : 0;
    const threshold = Number.isFinite(runtimeThreshold) && runtimeThreshold > 0
      ? runtimeThreshold
      : attrThreshold;
    // freeShippingPossible is only authoritative when the cart section supplied
    // it; without it, fall back to the rendered bar (which the same snippet
    // already gated on the country having a free-shipping rate at all).
    const possible = typeof cartData.freeShippingPossible === 'string'
      ? cartData.freeShippingPossible === 'true'
      : true;
    return { threshold: threshold * 100, offered: possible && threshold > 0 };
  };

  // Prices come out of the markup as text, and the separator is market
  // dependent: "£14.99" in GB but "€14,99" in DE and IT. Treat the LAST . or ,
  // as the decimal point when it has one or two digits after it, and anything
  // else as a thousands separator, so "€1.499,00" and "£1,499.00" both work.
  const parsePriceToCents = (value) => {
    const cleaned = (value || '').replace(/[^\d.,]/g, '');
    if (!cleaned) return null;

    const separator = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
    const decimals = separator === -1 ? 0 : cleaned.length - separator - 1;

    const normalised = separator !== -1 && decimals > 0 && decimals <= 2
      ? `${cleaned.slice(0, separator).replace(/[.,]/g, '')}.${cleaned.slice(separator + 1)}`
      : cleaned.replace(/[.,]/g, '');

    const parsed = parseFloat(normalised);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  };

  // layout/theme.liquid exposes a market-aware formatter built from
  // shop.money_format — "£14.99" in GB, "€14,99" in DE/IT. Use it so these
  // figures read the same as every other price on the page.
  const formatMoneyCents = (cents) => {
    const rounded = Math.round(cents);
    if (typeof window.formatMoney === 'function') return window.formatMoney(rounded);
    return (rounded / 100).toFixed(2);
  };

  // The subscription price carries its interval after a slash ("£14.99/mo",
  // or "£14.99/2 mo" for a 2-month supply such as Biotin). Drop the suffix
  // before parsing so the interval's digit never joins the amount.
  const getSelectedUnitPriceCents = () => {
    const option = getSelectedOption();
    const priceText = normalizeText(option?.querySelector('.price strong')?.textContent).split('/')[0];
    return parsePriceToCents(priceText);
  };

  const renderAtcTotal = () => {
    if (!atcTotalEl) return;
    const unitCents = getSelectedUnitPriceCents();
    if (unitCents === null) {
      atcTotalEl.textContent = '';
      return;
    }
    atcTotalEl.textContent = `· ${formatMoneyCents(unitCents * getKidsQuantity())}`;
  };

  const renderShipBar = () => {
    if (!shipBar || !shipBarFill) return;
    const { threshold, offered } = getShipBarConfig();
    // No free-shipping rate in this market — don't promise one the checkout
    // won't honour.
    if (!offered) {
      shipBar.hidden = true;
      return;
    }
    shipBar.hidden = false;

    // Actual cart total only — no projection of the line the stepper is about
    // to add. An early version counted it, which made this bar disagree with
    // the cart drawer ("£0.03 more" here vs "£10.02 more" there) and read as
    // already-nearly-unlocked for items not yet in the cart.
    const total = cartTotalInCents;
    const remaining = threshold - total;

    // Floor, and hold short of full until it genuinely unlocks — rounding let
    // £29.98 of a £30 goal paint a full bar next to "Spend £0.02 more".
    const percent = remaining <= 0
      ? 100
      : Math.min(99, Math.floor((total / threshold) * 100));
    shipBarFill.style.width = `${percent}%`;

    if (!shipBarMsg) return;

    if (remaining <= 0) {
      shipBar.classList.add('is-unlocked');
      shipBarMsg.textContent = shipBar.dataset.msgUnlocked || 'Free shipping unlocked';
      return;
    }

    shipBar.classList.remove('is-unlocked');
    const template = shipBar.dataset.msgProgress || 'Spend [amount] more for free shipping';
    shipBarMsg.textContent = template.replace('[amount]', formatMoneyCents(remaining));
  };

  const refreshBuyBoxTotals = () => {
    renderAtcTotal();
    renderShipBar();
  };

  setupKidsQtyStepper();
  refreshBuyBoxTotals();

  // Switching between Subscribe & Save and One-time changes the unit price, but
  // the theme moves the purchase-option--selected class on its own schedule —
  // later than the click handler and later than the next animation frame, so
  // refreshing on click read the outgoing price and left the button and bar
  // showing the subscription total after switching to One-time. Watch the class
  // itself instead, which is correct whenever that flip actually lands.
  if (purchaseOptions.length) {
    const selectionObserver = new MutationObserver(refreshBuyBoxTotals);
    purchaseOptions.forEach((option) => {
      selectionObserver.observe(option, { attributes: true, attributeFilter: ['class'] });
    });
  }

  // The inline cart calls window.NovominsPDP.setCartTotal() after every cart
  // refresh — that's how the night PDP keeps its own bar in step. Hook the same
  // contract so removing an item in the cart drawer moves this bar too, instead
  // of leaving the server-rendered total until the next page load. Chain onto
  // any existing implementation rather than replacing it (novomins-pdp.js
  // defines one on the PDPs where it loads).
  if (shipBar) {
    const previousPdpApi = window.NovominsPDP;
    window.NovominsPDP = {
      ...previousPdpApi,
      setCartTotal(amount) {
        if (previousPdpApi && typeof previousPdpApi.setCartTotal === 'function') {
          previousPdpApi.setCartTotal(amount);
        }
        const parsed = Number(amount);
        if (!Number.isFinite(parsed)) return;
        cartTotalInCents = Math.round(parsed * 100);
        renderShipBar();
      },
    };
  }

  const updateSellingPlanOnButtons = (sellingPlanId) => {
    addToCartButtons.forEach((button) => {
      button.setAttribute('data-selling-plan-id', sellingPlanId || 'null');
    });
  };

  const initialSelectedOption = getSelectedOption();
  if (initialSelectedOption) {
    const initialSellingPlanId = initialSelectedOption.getAttribute('data-selling-plan-id');
    updateSellingPlanOnButtons(initialSellingPlanId);
  }

  purchaseOptions.forEach(option => {
    option.addEventListener('click', function () {
      purchaseOptions.forEach(opt => opt.classList.remove('purchase-option--selected'));
      this.classList.add('purchase-option--selected');

      const sellingPlanId = this.getAttribute('data-selling-plan-id');
      updateSellingPlanOnButtons(sellingPlanId);
    });
  });

  addToCartButtons.forEach((addToCartButton) => {
    addToCartButton.addEventListener('click', async function () {
      const buttonText = this.querySelector('.button-text');
      const loadingSpinner = this.querySelector('.loading-overlay__spinner');
      const variantId = this.getAttribute('data-variant-id');
      const sellingPlanId = this.getAttribute('data-selling-plan-id');
      const addonVariantId = this.getAttribute('data-addon-variant-id');
      const addonVariantIds = (this.getAttribute('data-addon-variant-ids') || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

      if (!variantId) {
        console.error('Add to cart failed: missing variant id');
        return;
      }

      if (buttonText) {
        buttonText.style.display = 'none';
      }
      if (loadingSpinner) {
        loadingSpinner.classList.remove('hidden');
      }
      this.disabled = true;

      try {
        const primaryItem = {
          id: variantId,
          quantity: getKidsQuantity()
        };
        const itemsToAdd = [primaryItem];

        if (sellingPlanId && sellingPlanId !== 'null') {
          primaryItem.selling_plan = sellingPlanId;
        }

        if (addonVariantId) {
          const addonIdsToCheck = addonVariantIds.length ? addonVariantIds : [addonVariantId];
          const addonAlreadyInCart = await areAnyVariantsInCart(addonIdsToCheck);

          if (!addonAlreadyInCart) {
            itemsToAdd.push({
              id: addonVariantId,
              quantity: 1
            });
          }
        }

        await addItemsToCartWithDrawer(itemsToAdd);

        await getCartTotal();
      } catch (err) {
        console.error('Cart update failed:', err);
        showAddToCartError(this, err);
      } finally {
        if (buttonText) {
          buttonText.style.display = 'block';
        }
        if (loadingSpinner) {
          loadingSpinner.classList.add('hidden');
        }
        this.disabled = false;
      }
    });
  });

  // Inline message under the Add to Cart row. A 422 carries Shopify's own
  // shopper-facing description (stock, limits); anything else gets the
  // theme's translated generic cart error.
  let addToCartErrorTimer;
  function showAddToCartError(button, err) {
    const row = button.closest('.kids-atc-row');
    const errorEl = row && row.parentElement ? row.parentElement.querySelector('.add-to-cart__error') : null;
    if (!errorEl) return;
    const generic = (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
    const message = err && err.message;
    const isShopperMessage = message && !/^Add to cart failed/.test(message) && !/^(Failed to fetch|Load failed|NetworkError)/.test(message);
    errorEl.textContent = isShopperMessage ? message : generic;
    errorEl.hidden = false;
    clearTimeout(addToCartErrorTimer);
    addToCartErrorTimer = setTimeout(() => {
      errorEl.hidden = true;
    }, 6000);
  }

  async function addItemsToCartWithDrawer(items) {
    const requestItems = (items || [])
      .filter((item) => item && item.id)
      .map((item) => {
        const requestItem = {
          id: item.id,
          quantity: item.quantity || 1
        };

        if (
          typeof item.selling_plan !== 'undefined'
          && item.selling_plan !== null
          && item.selling_plan !== 'null'
        ) {
          requestItem.selling_plan = item.selling_plan;
        }

        return requestItem;
      });

    if (!requestItems.length) {
      throw new Error('Add to cart failed: missing line items');
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: requestItems })
    });

    if (!response.ok) {
      let errorMessage = 'Add to cart failed';
      try {
        const errorData = await response.json();
        if (errorData && errorData.description) {
          errorMessage = errorData.description;
        }
      } catch (e) {
        errorMessage = 'Add to cart failed';
      }

      throw new Error(errorMessage);
    }

    await response.json();

    const inlineCart = document.querySelector('.inline-cart');
    const inlineCartPageOverlay = document.querySelector('.inline-cart__page-overlay');

    if (inlineCart) {
      inlineCart.classList.add('active');
    }
    if (inlineCartPageOverlay) {
      inlineCartPageOverlay.classList.add('active');
    }
    document.body.classList.add('body--no-scroll');
  }

  async function areAnyVariantsInCart(variantIds) {
    const normalizedVariantIds = Array.from(
      new Set((variantIds || []).map((variantId) => String(variantId).trim()).filter(Boolean))
    );

    if (!normalizedVariantIds.length) {
      return false;
    }

    try {
      const cart = await fetchCartData();
      if (!cart || !Array.isArray(cart.items)) {
        return false;
      }

      return cart.items.some((item) => normalizedVariantIds.includes(String(item.variant_id)));
    } catch (err) {
      console.error('Error checking cart for addon variant:', err);
      return false;
    }
  }

  async function fetchCartData() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart data');
    }

    return response.json();
  }

  async function getCartTotal() {
    try {
      const cart = await fetchCartData();


      const freeShippingThreshold = window.cartData.freeShippingThreshold;
      const totalPriceInCents = cart.total_price;
      const totalPriceFormatted = (totalPriceInCents / 100).toFixed(2);

      // Keep the PDP free-shipping bar in step with the real cart after an add.
      cartTotalInCents = totalPriceInCents;
      renderShipBar();

      if (totalPriceFormatted >= freeShippingThreshold) {
        if (document.querySelector('.shipping-info__header--free')) {
          document.querySelector('.shipping-info__header--free').style.display = 'block';
        }
        if (document.querySelector('.shipping-info__header')) {
          document.querySelector('.shipping-info__header').style.display = 'none';
        }
      } else {
        if (document.querySelector('.shipping-info__header')) {
          document.querySelector('.shipping-info__header').style.display = 'block';
        }
        if (document.querySelector('.shipping-info__header--free')) {
          document.querySelector('.shipping-info__header--free').style.display = 'none';
        }
      }

      return totalPriceFormatted;
    } catch (err) {
      console.error('Error fetching cart total:', err);
      return null;
    }
  }


}

export default addToCart;